import { createSelector } from '@reduxjs/toolkit';
import { getIsMobile } from 'src/store/view/selectors';
import type { Coordinates } from '@lt/api/dist/types/map';
import { getBrowser } from '../browser/selectors';
import {
  getFromImmutableStateCompatible,
  getImmutableCompatible,
} from '../../../../../utils/immutableUtils';
import { getHotelsList } from '../hotels/selectors';
import { name } from './slice';
import type { MapState } from './types';

export const _mapSelector = (state) => getImmutableCompatible(state, name);
export const mapSelector =
  getFromImmutableStateCompatible<MapState>(_mapSelector);

/**
 * Список отелей с координатами
 */
export const getAllHotels = createSelector(mapSelector, (map) => map.hotels);

/**
 * Массив айдишников отелей
 */
export const getHotelsInList = createSelector(
  mapSelector,
  (map) => map.hotelsInList,
);

/**
 * Отель, на который наведен курсор или попап которого открыт на карте
 */
export const getActiveHotel = createSelector(
  mapSelector,
  (map) => map.activeHotel,
);

/**
 * Проверяет, нужно ли показывать кнопку раскрытия карты
 */
export const isExpandableMapSelector = createSelector(
  [getIsMobile, getBrowser],
  (isMobile, browser) => !isMobile && browser.lessThan.infinity,
);

/**
 * Свернута/развернута карта
 */
export const isExpandedMapSelector = createSelector(
  [mapSelector],
  (map) => map.expanded,
);

/**
 * Свернута/развернута карта (с проверкой масштаба браузера)
 */
export const isMapView = createSelector(
  [mapSelector, getBrowser],
  (map, browser) => Boolean(map.expanded && browser.lessThan.infinity),
);

/**
 * Мета инфа по отелю для попапа
 */
export const popupDataSelector = createSelector(
  [mapSelector],
  (map) => map.popupData,
);

/**
 * Список отелей с доп инфой
 */
export const getHotelsWithExtraInfo = createSelector(
  [getHotelsList],
  (allHotels) => {
    if (!allHotels.length) return [];
    return allHotels.map((h) => ({
      coordinates: [h.hotel.long, h.hotel.lat] as Coordinates,
      id: h.hotel.id,
      price: h.price,
      visited: h.isViewed,
    }));
  },
);

/**
 * Мета инфа из popupData + основная инфа по отелю для попапа
 */
export const popupDataWithHotelInfoSelector = createSelector(
  [popupDataSelector, getHotelsList],
  (popupData, hotelsList) => {
    if (!popupData) {
      return undefined;
    }

    const popupHotelId = Number(popupData.id);
    const hotelInfo = hotelsList.find(({ hotel }) => hotel.id === popupHotelId);

    const result = {
      ...popupData,
      hotelInfo,
    };

    return result;
  },
);

/**
 * Основная инфа по отелю для попапа
 */
export const hotelInfoForPopupSelector = createSelector(
  [popupDataWithHotelInfoSelector],
  (popupData) => popupData?.hotelInfo,
);
